// Componente genérico para operações de senha (reset e convite)
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Button, makeStyles, tokens } from '@fluentui/react-components';
import { usePasswordOperation, PasswordOperationParams } from '../../hooks/usePasswordOperation';
import { useValidation } from '../../hooks/useValidation';
import { securePasswordValidation, passwordValidation } from '../../utils/validationUtils';
import { ErrorHandler } from '../../utils/errorHandler';
import ForgotPasswordLayout from '../ForgotPassword/components/ForgotPasswordLayout';
import ResetPasswordForm from '../ForgotPassword/components/ResetPasswordForm';

interface PasswordOperationProps {
  params: PasswordOperationParams;
}

const useStyles = makeStyles({
  successContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground3,
  },
  successCard: {
    textAlign: 'center',
    padding: tokens.spacingVerticalXXL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    maxWidth: '400px',
  },
  successIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: tokens.colorPaletteGreenForeground1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: `${tokens.spacingVerticalM} auto`,
    color: tokens.colorNeutralForegroundOnBrand,
    fontSize: tokens.fontSizeHero700,
  },
  successTitle: {
    color: tokens.colorPaletteGreenForeground1,
    marginBottom: tokens.spacingVerticalM,
  },
  successMessage: {
    marginBottom: tokens.spacingVerticalXL,
    color: tokens.colorNeutralForeground2,
  },
  primaryButton: {
    width: '100%',
    marginTop: tokens.spacingVerticalM,
  },
  linkButton: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    textDecoration: 'none',
    borderRadius: tokens.borderRadiusSmall,
    fontSize: tokens.fontSizeBase300,
    display: 'inline-block',
    textAlign: 'center',
    ':hover': {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
  },
  errorContainer: {
    textAlign: 'center',
    padding: tokens.spacingVerticalXXL,
  },
  errorTitle: {
    marginBottom: tokens.spacingVerticalM,
    color: tokens.colorPaletteRedForeground1,
  },
  errorMessage: {
    marginBottom: tokens.spacingVerticalXL,
  },
});

const PasswordOperation: React.FC<PasswordOperationProps> = ({ params }) => {
  const styles = useStyles();
  const { type, email, token } = params;
  const {
    validateToken,
    executePasswordOperation,
    validateLoading,
    validateError,
    validateData,
    operationLoading,
    operationError
  } = usePasswordOperation();

  const [tokenValid, setTokenValid] = React.useState<boolean | null>(null);
  const [operationCompleted, setOperationCompleted] = React.useState(false);
  const validationExecutedRef = React.useRef(false);

  const validationRules = {
    password: [securePasswordValidation],
    confirmPassword: [passwordValidation],
  };

  const { getFieldProps, validateAll, errors, values } = useValidation(validationRules);

  // Para ambos os tipos (reset e invitation), validar token antecipadamente
  React.useEffect(() => {
    if (validationExecutedRef.current) {
      return; // Já executou a validação
    }

    validationExecutedRef.current = true;
    validateToken(email, token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intencionalmente vazio para executar apenas uma vez

  // Monitorar o resultado da validação para ambos os tipos
  React.useEffect(() => {
    if (validationExecutedRef.current && !validateLoading) {
      if (validateData !== null && validateData !== undefined) {
        if (typeof validateData === 'object' && validateData !== null && 'isValid' in validateData) {
          setTokenValid(validateData.isValid);
        } else {
          setTokenValid(false);
        }
      } else if (validateError) {
        setTokenValid(false);
      }
    }
  }, [validateData, validateError, validateLoading]);

  // Redirecionamento automático após operação completada (apenas para invitation)
  React.useEffect(() => {
    if (operationCompleted && type === 'invitation') {
      const timer = setTimeout(() => {
        window.location.href = '/login';
      }, 3000); // 3 segundos

      return () => clearTimeout(timer);
    }
  }, [operationCompleted, type]);

  // Validação personalizada para confirmar senha
  React.useEffect(() => {
    if (values.confirmPassword && values.password !== values.confirmPassword) {
      errors.confirmPassword = 'As senhas não coincidem';
    } else if (values.confirmPassword && values.password === values.confirmPassword) {
      delete errors.confirmPassword;
    }
  }, [values.password, values.confirmPassword, errors]);

  // Processa erros usando o ErrorHandler padronizado
  const processedError = React.useMemo(() => {
    if (!operationError) return null;

    if (typeof operationError === 'string') {
      return operationError;
    }

    return ErrorHandler.getGeneralError(operationError);
  }, [operationError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateAll()) {
      return;
    }

    if (values.password !== values.confirmPassword) {
      return;
    }

    try {
      await executePasswordOperation(values.password, values.confirmPassword, email, token, type);
      setOperationCompleted(true);
    } catch (error) {
      console.error(`Erro ao executar operação ${type}:`, error);
    }
  };

  const handleBackToLogin = () => {
    window.location.href = '/login';
  };

  // Determinar títulos e mensagens baseado no tipo
  const getContent = () => {
    if (type === 'reset') {
      return {
        title: 'Redefinir Senha',
        description: 'Digite sua nova senha abaixo.',
        successTitle: 'Senha Redefinida!',
        successMessage: 'Sua senha foi alterada com sucesso. Você já pode fazer login com sua nova senha.',
        invalidTitle: 'Link Inválido ou Expirado',
        invalidMessage: 'Este link de redefinição de senha é inválido ou já expirou. Solicite um novo link de recuperação.',
        showEmail: false
      };
    } else {
      return {
        title: 'Bem-vindo!',
        description: 'Você foi convidado para usar o sistema. Defina sua senha para começar.',
        successTitle: 'Convite Aceito!',
        successMessage: 'Sua senha foi definida com sucesso. Você será redirecionado para a página de login em alguns segundos.',
        invalidTitle: 'Convite Inválido',
        invalidMessage: 'Este link de convite é inválido ou está incompleto. Verifique se você copiou o link completo do email.',
        showEmail: true
      };
    }
  };

  const content = getContent();

  // Mostrar loading enquanto valida o token
  if (tokenValid === null) {
    return (
      <ForgotPasswordLayout>
        <div className={styles.errorContainer}>
          <p>Validando link...</p>
        </div>
      </ForgotPasswordLayout>
    );
  }

  // Token/link inválido
  if (tokenValid === false) {
    return (
      <ForgotPasswordLayout>
        <div className={styles.errorContainer}>
          <h2 className={styles.errorTitle}>{content.invalidTitle}</h2>
          <p className={styles.errorMessage}>
            {content.invalidMessage}
          </p>
          <Link to="/login" className={styles.linkButton}>
            Voltar ao Login
          </Link>
        </div>
      </ForgotPasswordLayout>
    );
  }

  // Operação completada com sucesso
  if (operationCompleted) {
    if (type === 'invitation') {
      // Layout especial para convite aceito
      return (
        <div className={styles.successContainer}>
          <div className={styles.successCard}>
            <div className={styles.successIcon}>
              ✓
            </div>
            <h2 className={styles.successTitle}>{content.successTitle}</h2>
            <p className={styles.successMessage}>
              {content.successMessage}
            </p>
            <Button
              appearance="primary"
              className={styles.primaryButton}
              onClick={() => window.location.href = '/login'}
            >
              Ir para Login Agora
            </Button>
          </div>
        </div>
      );
    } else {
      // Layout para reset de senha
      return (
        <ForgotPasswordLayout>
          <div className={styles.errorContainer}>
            <h2 className={styles.successTitle}>{content.successTitle}</h2>
            <p className={styles.errorMessage}>
              {content.successMessage}
            </p>
            <Link to="/login" className={styles.linkButton}>
              Fazer Login
            </Link>
          </div>
        </ForgotPasswordLayout>
      );
    }
  }

  // Renderizar formulário com layout consistente
  return (
    <ForgotPasswordLayout>
      {content.showEmail && (
        <div style={{ textAlign: 'center', marginBottom: tokens.spacingVerticalXL }}>
          <h2 style={{ marginBottom: tokens.spacingVerticalS, color: tokens.colorNeutralForeground1 }}>{content.title}</h2>
          <p style={{ color: tokens.colorNeutralForeground2, marginBottom: tokens.spacingVerticalM }}>
            {content.description}
          </p>
          <div style={{
            padding: tokens.spacingVerticalS,
            backgroundColor: tokens.colorNeutralBackground3,
            borderRadius: tokens.borderRadiusSmall,
            fontSize: tokens.fontSizeBase200,
            color: tokens.colorNeutralForeground2
          }}>
            <strong>E-mail:</strong> {email}
          </div>
        </div>
      )}

      <ResetPasswordForm
        getFieldProps={getFieldProps}
        errors={errors}
        onSubmit={handleSubmit}
        loading={operationLoading}
        error={processedError}
        onBackToLogin={handleBackToLogin}
      />
    </ForgotPasswordLayout>
  );
};

export default PasswordOperation;